/**
 * Free-pattern download analytics publisher.
 *
 * Publishes exactly one `nysta:free_pattern_download` customer event per user
 * activation of a free-pattern download action, so a Shopify Custom Pixel can
 * subscribe to it.
 *
 * This file only publishes. It does not initialise GA4, does not load any external
 * script, and does not forward anything to Google directly -- forwarding is the
 * pixel's job and is deliberately not implemented here. No page-view, purchase or
 * standard ecommerce event is ever emitted.
 *
 * Duplicate publishing is prevented at registration time -- exactly one delegated
 * listener is installed, guarded so that re-executing this module cannot install a
 * second one. There is deliberately no time-based suppression: two genuine clicks
 * publish two events no matter how quickly they follow each other.
 *
 * The download itself is a plain anchor with a real href, so it works with
 * JavaScript disabled. This listener is passive and never calls preventDefault(),
 * so an analytics failure can neither block nor delay navigation.
 */
(() => {
  const EVENT_NAME = 'nysta:free_pattern_download';
  const SELECTOR = '[data-free-pattern-download]';
  const GUARD = '__nystaFreePatternDownloadBound';

  // Idempotent initialisation: if this module is evaluated more than once (duplicate
  // script tag, section re-render, bfcache restore), the listener is installed once.
  if (window[GUARD]) return;
  window[GUARD] = true;

  /** @param {HTMLElement} el */
  const publish = (el) => {
    try {
      const analytics = typeof Shopify !== 'undefined' ? Shopify.analytics : null;
      if (!analytics || typeof analytics.publish !== 'function') return;

      analytics.publish(EVENT_NAME, {
        product_id: el.dataset.productId || '',
        handle: el.dataset.handle || '',
        pattern_id: el.dataset.patternId || '',
        placement: el.dataset.placement || '',
        source_page: 'product',
      });
    } catch (error) {
      // Analytics must never break the download. Swallow and continue.
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      try {
        const target = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
        if (!target) return;

        // One activation -> one event. Keyboard activation (Enter on a focused anchor)
        // dispatches a click, so it travels this same path.
        publish(target);
      } catch (error) {
        // Never interfere with the anchor's default navigation.
      }
    },
    // Passive: this listener cannot call preventDefault(), so the browser follows the
    // link immediately regardless of what happens above.
    { passive: true }
  );
})();
